import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, TextField } from '@mui/material';

import {
  completeSecurityStepUp,
  getSecuritySettings,
  issueFederatedContactVerification,
  issueSecurityStepUp,
  verifyFederatedContact,
  SecuritySettings as Settings,
  updateNotificationPreference,
} from '@/api/securitySettings.api';
import {
  getExternalIdentities,
  startExternalIdentityLink,
  startExternalReauth,
} from '@/api/externalAuth.api';
import type { ExternalProviderDescriptor } from '@/types/externalAuth';
import PageContainer from '@/components/Container/PageContainer';
import InnerPageTitle from '@/components/UI/InnerPageTitle';
import { Button } from '@/components/UI/Button';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import {
  ButtonCluster,
  ContactForm,
  EmptyState,
  ItemContent,
  PreferenceTable,
  SectionHeader,
  SectionActions,
  SettingsLayout,
  SettingsList,
  SettingsRow,
  SettingsSection,
  TableScroll,
  VerificationPanel,
} from './styles';

type StepUpAction = 'set_default_factor' | 'verify_phone' | 'unlink_wechat' | 'link_external';
type PendingOtp = { action: StepUpAction; challengeId: string; targetProvider?: string };

const SecuritySettings = () => {
  // @ts-ignore -- the repository's fully inferred i18n catalogue exceeds TypeScript's instantiation depth.
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [providers, setProviders] = useState<ExternalProviderDescriptor[]>([]);
  const [pending, setPending] = useState<PendingOtp | null>(null);
  const [code, setCode] = useState('');
  const [contactType, setContactType] = useState<'email' | 'sms'>('email');
  const [contactValue, setContactValue] = useState('');
  const [contactCountryCode, setContactCountryCode] = useState<'+852' | '+86'>('+852');
  const [pendingContactChallenge, setPendingContactChallenge] = useState<string | null>(null);
  const [contactCode, setContactCode] = useState('');

  const load = async () => {
    try {
      const response = await getSecuritySettings();
      setSettings(response.data.data);
      if (process.env.REACT_APP_EXTERNAL_AUTH_ENABLED === 'true') {
        const external = await getExternalIdentities();
        setProviders(external.data.data.providers);
      }
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pages.securitySettings.loadError'));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const stepUpFactor = useMemo(() => settings?.factors.find((factor) => factor.is_default && factor.verified)
    || settings?.factors.find((factor) => factor.verified), [settings]);
  const linkedProviders = settings?.external_identities.map((item) => item.provider) || [];
  const providerName = (provider: string) => {
    const descriptor = providers.find((item) => item.key === provider);
    const locale = (['en', 'tc', 'sc'].includes(i18n.language) ? i18n.language : 'en') as 'en' | 'tc' | 'sc';
    return descriptor?.display_name[locale] || descriptor?.display_name.en || provider;
  };

  const startOtp = async (factorId: number, action: StepUpAction, targetFactorId?: number, targetProvider?: string) => {
    try {
      const response = await issueSecurityStepUp(factorId, action, targetFactorId);
      setPending({ action, challengeId: response.data.data.challenge_id, targetProvider });
      setCode('');
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.message);
    }
  };

  const finishOtp = async () => {
    if (!pending || !/^\d{6}$/.test(code)) return;
    try {
      if (pending.action === 'link_external' && pending.targetProvider) {
        const response = await startExternalIdentityLink(pending.targetProvider, {
          challenge_id: pending.challengeId,
          code,
        });
        window.location.assign(response.data.data.authorization_url);
        return;
      }
      await completeSecurityStepUp(pending.action as Exclude<StepUpAction, 'link_external'>, pending.challengeId, code);
      successHandler(t('pages.securitySettings.updateSuccess'));
      setPending(null);
      await load();
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pages.securitySettings.verificationFailed'));
    }
  };

  const beginLink = async (targetProvider: string) => {
    try {
      if (linkedProviders.length > 0) {
        const sourceProvider = linkedProviders[0];
        window.sessionStorage.setItem('external.pendingSecurityAction', `link:${targetProvider}`);
        const response = await startExternalReauth(sourceProvider, `link:${targetProvider}`);
        window.location.assign(response.data.data.authorization_url);
        return;
      }
      if (!stepUpFactor) {
        errorHandler(t('pages.securitySettings.noReauthFactor'));
        return;
      }
      await startOtp(stepUpFactor.id, 'link_external', undefined, targetProvider);
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pages.securitySettings.updateError'));
    }
  };

  const beginUnlink = async (targetProvider: string) => {
    try {
      const sourceProvider = linkedProviders.find((provider) => provider !== targetProvider) || targetProvider;
      window.sessionStorage.setItem('external.pendingSecurityAction', `unlink:${targetProvider}`);
      const response = await startExternalReauth(sourceProvider, `unlink:${targetProvider}`);
      window.location.assign(response.data.data.authorization_url);
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pages.securitySettings.updateError'));
    }
  };

  const channelAvailable = (channel: string) => {
    if (channel === 'email' || channel === 'sms') {
      return settings?.factors.some((factor) => factor.type === channel && factor.verified) ?? false;
    }
    if (channel === 'web_push') return false;
    return true;
  };
  const preference = (category: string, channel: string) => channelAvailable(channel)
    && (settings?.notification_preferences.find((item) => item.category === category && item.channel === channel)?.enabled ?? true);
  const categories = [
    { key: 'security', label: t('pages.securitySettings.categories.security') },
    { key: 'account', label: t('pages.securitySettings.categories.account') },
    { key: 'case', label: t('pages.securitySettings.categories.case') },
    { key: 'transaction', label: t('pages.securitySettings.categories.transaction') },
    { key: 'marketing', label: t('pages.securitySettings.categories.marketing') },
  ] as const;
  const changePreference = async (category: string, channel: string, enabled: boolean) => {
    try {
      await updateNotificationPreference(category, channel, enabled);
      await load();
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pages.securitySettings.updateError'));
    }
  };

  const issueContact = async () => {
    try {
      const response = await issueFederatedContactVerification(
        contactType,
        contactType === 'email'
          ? { email: contactValue }
          : { country_code: contactCountryCode, phone: contactValue }
      );
      setPendingContactChallenge(response.data.data.challenge_id);
      setContactCode('');
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pages.securitySettings.contactVerificationFailed'));
    }
  };

  const verifyContact = async () => {
    if (!pendingContactChallenge || !/^\d{6}$/.test(contactCode)) return;
    try {
      await verifyFederatedContact(pendingContactChallenge, contactCode);
      setPendingContactChallenge(null);
      setContactValue('');
      setContactCode('');
      successHandler(t('pages.securitySettings.contactVerified'));
      await load();
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pages.securitySettings.contactVerificationFailed'));
    }
  };

  const removeContact = async (type: 'email' | 'sms') => {
    const sourceProvider = linkedProviders[0];
    if (!sourceProvider) {
      errorHandler(t('pages.securitySettings.noReauthFactor'));
      return;
    }
    try {
      window.sessionStorage.setItem('external.pendingSecurityAction', `remove_contact:${type}`);
      const response = await startExternalReauth(sourceProvider, `remove_contact:${type}`);
      window.location.assign(response.data.data.authorization_url);
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pages.securitySettings.updateError'));
    }
  };

  const availableProviders = providers.filter((provider) => provider.enabled_for_linking && !linkedProviders.includes(provider.key));
  return <PageContainer title={<InnerPageTitle link="/home" subtitle={t('pages.securitySettings.subtitle')} />}>
    <SettingsLayout>
      {!settings && <SettingsSection aria-live="polite">
        <EmptyState>{t('loading')}</EmptyState>
      </SettingsSection>}

      {settings?.factors.length ? <SettingsSection aria-labelledby="sign-in-factors-title">
        <SectionHeader><h2 id="sign-in-factors-title">{t('pages.securitySettings.signInFactors')}</h2></SectionHeader>
        <SettingsList>{settings.factors.map((factor) => <SettingsRow key={factor.id}>
          <ItemContent>
            <strong>{factor.type.toUpperCase()} · {factor.masked_destination}</strong>
            <span>{factor.verified ? t('pages.securitySettings.verified') : t('pages.securitySettings.verificationRequired')}
              {factor.is_default && ` · ${t('pages.securitySettings.default')}`}</span>
          </ItemContent>
          <ButtonCluster>
            {!factor.is_default && factor.verified && stepUpFactor && stepUpFactor.id !== factor.id
              && <Button type="button" size="sm" variant="primary-invert" rounded={false}
                onClick={() => startOtp(stepUpFactor.id, 'set_default_factor', factor.id)}>{t('pages.securitySettings.setDefault')}</Button>}
            {factor.type === 'sms' && !factor.verified
              && <Button type="button" size="sm" variant="primary-invert" rounded={false}
                onClick={() => startOtp(factor.id, 'verify_phone')}>{t('pages.securitySettings.verifyPhone')}</Button>}
          </ButtonCluster>
        </SettingsRow>)}</SettingsList>
      </SettingsSection> : null}

      {settings && <SettingsSection aria-labelledby="external-identities-title">
        <SectionHeader>
          <h2 id="external-identities-title">{t('pages.securitySettings.externalIdentities')}</h2>
          <p>{t('pages.securitySettings.externalIdentitiesHelp')}</p>
        </SectionHeader>
        {settings?.external_identities.length ? <SettingsList>{settings.external_identities.map((identity) => <SettingsRow key={identity.provider}>
          <ItemContent><strong>{providerName(identity.provider)}</strong><span>{t('pages.securitySettings.linked')}</span></ItemContent>
          <ButtonCluster><Button type="button" size="sm" variant="primary-invert" rounded={false}
            disabled={settings.account_origin === 'federated' && settings.external_identities.length === 1}
            onClick={() => beginUnlink(identity.provider)}>{t('pages.securitySettings.unlink')}</Button></ButtonCluster>
        </SettingsRow>)}</SettingsList> : <EmptyState>{t('pages.securitySettings.noExternalIdentity')}</EmptyState>}
        {availableProviders.length > 0 && <SectionActions>
          {availableProviders.map((provider) => <Button key={provider.key} type="button" size="sm" variant="primary-invert"
            rounded={false} onClick={() => beginLink(provider.key)}>
            {t('pages.securitySettings.linkProvider', { provider: providerName(provider.key) })}
          </Button>)}
        </SectionActions>}
      </SettingsSection>}

      {pending && <SettingsSection aria-labelledby="verification-code-title" aria-live="polite">
        <SectionHeader><h2 id="verification-code-title">{t('pages.securitySettings.enterCode')}</h2></SectionHeader>
        <VerificationPanel>
          <TextField fullWidth variant="standard" name="security-verification-code"
            label={t('pages.securitySettings.enterCode')} value={code} onChange={(event) => setCode(event.target.value)}
            autoComplete="one-time-code" inputProps={{ inputMode: 'numeric', maxLength: 6 }} />
          <Button type="button" size="sm" variant="primary" rounded={false} disabled={!/^\d{6}$/.test(code)}
            onClick={finishOtp}>{t('pages.securitySettings.confirm')}</Button>
        </VerificationPanel>
      </SettingsSection>}

      {settings?.account_origin === 'federated' && <SettingsSection aria-labelledby="optional-contacts-title">
        <SectionHeader>
          <h2 id="optional-contacts-title">{t('pages.securitySettings.optionalContacts')}</h2>
          <p>{t('pages.securitySettings.optionalContactsHelp')}</p>
        </SectionHeader>
        {settings.factors.filter((factor) => factor.verified).length > 0 && <SettingsList>
          {settings.factors.filter((factor) => factor.verified).map((factor) => <SettingsRow key={`contact-${factor.id}`}>
            <ItemContent><strong>{factor.type.toUpperCase()}</strong><span>{factor.masked_destination}</span></ItemContent>
            <ButtonCluster><Button type="button" size="sm" variant="primary-invert" rounded={false}
              onClick={() => removeContact(factor.type)}>{t('pages.securitySettings.removeContact')}</Button></ButtonCluster>
          </SettingsRow>)}
        </SettingsList>}
        <ContactForm className={contactType === 'sms' ? 'with-country-code' : undefined}>
          <TextField select fullWidth variant="standard" name="contact-type" label={t('pages.securitySettings.contactType')}
            value={contactType} onChange={(event) => setContactType(event.target.value as 'email' | 'sms')}>
            <MenuItem value="email">Email</MenuItem><MenuItem value="sms">SMS</MenuItem>
          </TextField>
          {contactType === 'sms' && <TextField select fullWidth variant="standard" name="contact-country-code"
            label={t('form.countryCode.label')} value={contactCountryCode}
            onChange={(event) => setContactCountryCode(event.target.value as '+852' | '+86')}>
            <MenuItem value="+852">+852</MenuItem><MenuItem value="+86">+86</MenuItem>
          </TextField>}
          <TextField fullWidth variant="standard" name="contact-value" label={contactType === 'email'
            ? t('pages.securitySettings.email') : t('pages.securitySettings.sms')}
            value={contactValue} onChange={(event) => setContactValue(event.target.value)}
            type={contactType === 'email' ? 'email' : 'tel'} autoComplete={contactType === 'email' ? 'email' : 'tel'} />
          <Button type="button" size="sm" variant="primary-invert" rounded={false} disabled={!contactValue.trim()}
            onClick={issueContact}>{t('pages.securitySettings.verifyContact')}</Button>
        </ContactForm>
        {pendingContactChallenge && <VerificationPanel aria-live="polite">
          <TextField fullWidth variant="standard" name="contact-verification-code"
            label={t('pages.securitySettings.enterCode')} value={contactCode}
            onChange={(event) => setContactCode(event.target.value)} autoComplete="one-time-code"
            inputProps={{ inputMode: 'numeric', maxLength: 6 }} />
          <Button type="button" size="sm" variant="primary" rounded={false} disabled={!/^\d{6}$/.test(contactCode)}
            onClick={verifyContact}>{t('pages.securitySettings.confirm')}</Button>
        </VerificationPanel>}
      </SettingsSection>}


      {settings && <SettingsSection aria-labelledby="notification-preferences-title">
        <SectionHeader>
          <h2 id="notification-preferences-title">{t('pages.securitySettings.notificationPreferences')}</h2>
          <p>{t('pages.securitySettings.notificationPreferencesHelp')}</p>
        </SectionHeader>
        <TableScroll tabIndex={0} aria-label={t('pages.securitySettings.notificationPreferences')}>
          <PreferenceTable><thead><tr>
            <th scope="col">{t('pages.securitySettings.category')}</th><th scope="col">{t('pages.securitySettings.inApp')}</th>
            <th scope="col">{t('pages.securitySettings.email')}</th><th scope="col">{t('pages.securitySettings.sms')}</th>

          </tr></thead><tbody>{categories.map((category) => <tr key={category.key}><th scope="row">{category.label}</th>
            {(['in_app', 'email', 'sms'] as const).map((channel) => <td key={channel}>
              <input type="checkbox" checked={preference(category.key, channel)}
                disabled={(category.key !== 'marketing' && channel === 'in_app') || !channelAvailable(channel)}
                onChange={(event) => changePreference(category.key, channel, event.target.checked)}
                aria-label={`${category.label} ${channel}`} />
            </td>)}</tr>)}</tbody></PreferenceTable>
        </TableScroll>
      </SettingsSection>}
    </SettingsLayout>
  </PageContainer>;
};

export default SecuritySettings;
